import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {NotificationService} from '@services/notification/notification.service';
import {MachineService} from '@services/administration/machine/machine.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';

import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';

import {
  ImportSettingsComponent
} from '@components/administration/machine/modals/import-settings/import-settings.component';
import {
  MachineCloningInfoComponent
} from '@components/administration/machine/modals/machine-cloning-info/machine-cloning-info.component';

import {Machine} from '@models/machine/machine';
import {Organization} from '@models/organization/organization';
import {MachineGroup} from '@models/adminstration/machine-group/machine-group';
import {TableauProject} from '@models/machine/tableau-project';
import {MachineCopySettings} from '@models/machine/machine-copy-settings';
import {ImportSettings} from '@models/machine/import-settings';
import {MachineAddProperties} from '@models/machine/machine-add-properties';
import {ChartConfig} from '@models/machine/chart-config';
import {GOOGLE_DATASET_LOCATIONS} from '@constants/machine/google_dataset_locations';
import {LocationDetails} from '@models/locationDetails/location-details';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {
  FavoriteNotificationService
} from '@services/left-menu/favorites/favorite-notification/favorite-notification.service';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {MachineCategoryService} from '@services/administration/portal-setup/machine-category/machine-category.service';
import {MachineCategory} from '@models/adminstration/portal-setup/machine-category/machine-category';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {USER_ROLE} from '@constants/user-role.constant';
import {ElasticSearchResult} from '@models/elastic-search/response/elastic-search-result';
import {ModalService} from '@services/modal/modal.service';
import {BIGQUERY_VALID_NAME_REGEX} from '@constants/reg-exp';

@Component({
  selector: 'scitis-admin-machine-edit',
  templateUrl: './machine-edit.component.html',
  styleUrls: ['./machine-edit.component.scss'],
  standalone: false
})
export class MachineEditComponent implements OnInit, OnDestroy {

  @Input()
  firebaseUserId: string = '';

  @Input()
  machine: Machine = new Machine();

  @Input()
  userRole: Role = new Role(new User());

  @Input()
  machines: Machine[] = new Array<Machine>();

  @Input()
  loggedUser: User = new User();

  isNewMachine: boolean = false;
  submitted: boolean = false;
  isLoadingOrganizations: boolean = false;
  isLoadingMachineGroups: boolean = false;
  isLoadingCategories: boolean = false;
  isLoadingTableauProjects: boolean = false;
  possibleOrganizations: Organization[] = new Array<Organization>();
  possibleMachineGroups: MachineGroup[] = new Array<MachineGroup>();
  possibleCategories: MachineCategory[] = [];
  possibleTableauProjects: TableauProject[] = new Array<TableauProject>();
  displayGroup: boolean = false;
  locationDetails: LocationDetails = new LocationDetails;
  locationDetailsFormIsInvalid = false;
  copySetting: MachineCopySettings = new MachineCopySettings();
  copy: boolean = false;
  selectedMachineType: string = '';
  sourceMachineId: number | undefined = undefined;
  sourceMachineName: string = '';
  selectedOrganizationId: number = this.machine.parentId || -1;
  usedTables: string[] = new Array<string>();
  machineForm: UntypedFormGroup = new UntypedFormGroup({});
  initialMachineType = '';

  public GOOGLE_DATASET_LOCATIONS: string[] = GOOGLE_DATASET_LOCATIONS;
  public gatewayDetailsInvalid: boolean = false;
  public gatewayDetails: Gateway = new Gateway();
  private COMPONENT_NAME: string = 'MachineEditComponent';
  private readonly machineConflictTitle = this.translateService.instant('Machine name conflict');

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
      private activeModal: NgbActiveModal,
      private sortingService: SortingService,
      private translateService: TranslateService,
      private notificationService: NotificationService,
      private elasticSearchService: ElasticSearchService,
      private machineService: MachineService,
      private loadingIndicator: LoadingIndicatorService,
      private modalService: ModalService,
      private menuNotificationService: MenuNotificationService,
      private machineCategoryService: MachineCategoryService,
      private favoriteNotificationService: FavoriteNotificationService) {
    this.machineConflictTitle = this.translateService.instant('Machine name conflict');
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.machineForm.controls;
  }

  get isDisabledSaveBtn(): boolean {
    const isFormValid = this.machineForm.invalid || this.locationDetailsFormIsInvalid;
    return this.submitted ? isFormValid : this.submitted && isFormValid;
  };

  private static copyMachineProperties(source: Machine, target: Machine | null): Machine {
    if (target === null) {
      target = new Machine();
    }
    target.id = source.id;
    target.location = source.location;
    target.name = source.name;
    target.parentId = source.parentId;
    target.type = source.type;
    target.category = source.category;
    target.chartConfig = source.chartConfig;
    target.dataSourcePartitioning = source.dataSourcePartitioning;
    target.tenant = source.tenant;
    target.trafficLightConfiguration = source.trafficLightConfiguration;
    target.projectId = source.projectId;
    target.datasetLocation = source.datasetLocation;
    target.tableauProjectId = source.tableauProjectId;
    target.machineTag = source.machineTag;
    target.streamToPubSub = source.streamToPubSub;
    target.triggerExecutionInDataflow = source.triggerExecutionInDataflow;
    target.liveStreamingInDataflow = source.liveStreamingInDataflow;
    target.dataWarehouseStreamingInDataflow = source.dataWarehouseStreamingInDataflow;
    target.group = source.group;
    target.externalId = source.externalId;
    target.parentExternalId = source.parentExternalId;
    target.locationDetails = source.locationDetails;
    target.templateMachine = source.templateMachine;

    return target;
  }

  ngOnInit(): void {
    this.initialMachineType = this.machine.type;
    this.generateMachineForm(this.machine);
    if (this.machine.id === undefined) {
      this.isNewMachine = true;
    }
    this.setPossibleOrganizations();
    this.setPossibleMachineGroupNames();
    this.setPossibleCategories();
    this.setDefaultGoogleProjectId();
    this.locationDetails = this.machine.locationDetails;
    this.selectedOrganizationId = this.machine.parentId;
    this.loadTableauProjectsForOrganization(this.selectedOrganizationId);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onClose(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onOrganizationChange(organization: Organization): void {
    this.selectedOrganizationId = organization.id;
    this.loadTableauProjectsForOrganization(organization.id);
  }

  importSettings(): void {
    const modalRef = this.modalService.open(ImportSettingsComponent, {});
    modalRef.componentInstance.machine = this.machine;
    modalRef.componentInstance.machines = this.machines;
    modalRef.componentInstance.selectedMachineType = this.selectedMachineType;
    modalRef.componentInstance.loggedUser = this.loggedUser;
    modalRef.result.then((result: ImportSettings) => {
      let selectedMachine = new Machine();
      this.sourceMachineId = undefined;
      this.sourceMachineName = '';
      if (result.machineType !== '') {
        selectedMachine = this.machines.filter(m => m.type === result.machineType)[0];
        this.copyPropertiesFromSelectedMachine(selectedMachine);
      } else {
        this.machineForm.controls.parentId.setValue(result.organization);
        this.loadTableauProjectsForOrganization(result.organization);
        this.machineForm.controls.category.setValue(result.category);
        this.machineForm.controls.group.setValue(result.group);
      }
      this.copySettingsFromMachine(selectedMachine, result);
      this.setTables(selectedMachine);
    });
  }

  onSave(): void {
    this.submitted = true;
    // stop here if form is invalid
    if (this.machineForm.invalid || this.locationDetailsFormIsInvalid || this.gatewayDetailsInvalid) {
      return;
    }
    if (this.machine.id === undefined) {
      this.onAddingMachine();
    } else {
      this.updateMachine();
    }
  }

  onUpdatedLocationDetailsEvent(updatedLocationDetails: LocationDetails) {
    this.machine.locationDetails = updatedLocationDetails;
    if (updatedLocationDetails.countryCode && updatedLocationDetails.regionCity &&
        updatedLocationDetails.countryCode.length > 0 && updatedLocationDetails.regionCity.length > 0) {
      this.machine.location = updatedLocationDetails.countryCode + ', ' + updatedLocationDetails.regionCity;
    }
  }

  onFormIsValid(locationDetailsFormIsInvalid: boolean) {
    this.locationDetailsFormIsInvalid = !locationDetailsFormIsInvalid;
  }

  onNameChanged() {
    this.setValidationErrorWhenNameNotUnique();
  }

  public onMachineCategoryChange(category: MachineCategory) {
    this.machine.category = category.name;
    if (category.id) {
      this.machine.machineCategoryId = category.id;
    }
  }

  public onMachineTagChanged(tag: string) {
    this.machine.machineTag = tag;
    this.f.machineTag.setValue(tag);
    this.f.machineTag.markAsDirty();
  }

  public onIsMachineTemplateChange(isTemplate: boolean) {
    this.machine.templateMachine = isTemplate;
    this.f.isTemplate.setValue(isTemplate);
  }

  public onDefaultGatewayDetailsChanged($event: Gateway) {
    this.gatewayDetails = $event;
  }

  public onDefaultGatewayDetailsIssues($event: boolean) {
    this.gatewayDetailsInvalid = $event;
  }

  public isAdminOrHigher(): boolean {
    return (this.userRole.isAdminRole || this.userRole.isTechAdminRole || this.userRole.isSuperAdminRole);
  }

  private loadTableauProjectsForOrganization(organizationId: number) {
    if (!organizationId || organizationId < 0) {
      return;
    }
    this.isLoadingTableauProjects = true;
    this.machineService.listTableauProjects(-1, organizationId).subscribe(
        {
          next: (tableauProjects: TableauProject[]) => {
            this.possibleTableauProjects = tableauProjects;
            this.isLoadingTableauProjects = false;
          }, error: error => {
            const message = this.translateService.instant('Error getting tableau projects list.');
            this.notificationService.error(message, error, this.COMPONENT_NAME, true);
            this.isLoadingTableauProjects = false;
          }
        }
    );
  }

  private generateMachineForm(machine: Machine): void {
    this.machineForm = new UntypedFormGroup({
      type: new UntypedFormControl(machine.type, [Validators.required]),
      name: new UntypedFormControl({
        value: machine.name,
        disabled: machine.id !== undefined
      }, [Validators.required, Validators.pattern(BIGQUERY_VALID_NAME_REGEX)]),
      projectId: new UntypedFormControl(machine.projectId, [Validators.required]),
      category: new UntypedFormControl({
        value: machine.category,
        disabled: machine.id !== undefined
      }, [Validators.required]),
      tableauProjectId: new UntypedFormControl(machine.tableauProjectId),

      parentId: new UntypedFormControl({
        value: machine.parentId,
        disabled: machine.id !== undefined
      }, [Validators.required, Validators.min(1)]),
      group: new UntypedFormControl(machine.group, [Validators.required]),
      externalId: new UntypedFormControl(machine.externalId),
      machineTag: new UntypedFormControl(machine.machineTag),
      isTemplate: new UntypedFormControl(machine.templateMachine),
      datasetLocation: new UntypedFormControl(machine.datasetLocation ? machine.datasetLocation : GOOGLE_DATASET_LOCATIONS[2], [Validators.required])
    });

    if (machine.id !== undefined) {
      this.displayGroup = false;
      this.machineForm.get('group')?.setValidators(null);
    } else {
      this.displayGroup = true;
      this.machineForm.get('group')?.setValidators([Validators.required]);
    }

    if (!this.isAdminOrHigher()) {
      this.machineForm.disable();
    }
  }

  private setPossibleOrganizations(): void {
    this.isLoadingOrganizations = true;
    const rootOrganizationId = this.loggedUser.role === USER_ROLE.SUPERADMIN ? -1 : this.loggedUser.parentId;
    this.elasticSearchService.listAllOrganizations(rootOrganizationId).subscribe(
        {
          next: (success: ElasticSearchResponseEntity) => {
            if (success?.result?.data && success.result.data.length > 0) {
              const result: ElasticSearchResult = success.result;
              this.possibleOrganizations = [...result.data];
              this.possibleOrganizations.sort((a, b) => this.sortingService.sortByName(a, b));
            }

            this.isLoadingOrganizations = false;
          }, error: error => {
            const message = this.translateService.instant('Error getting organizations list.');
            this.notificationService.error(message, error, this.COMPONENT_NAME, true);
            this.isLoadingOrganizations = false;
          }
        }
    );
  }

  private setDefaultGoogleProjectId(): void {
    if (this.machine.projectId && !this.machine.projectId.isNullOrWhitespace()) {
      return;
    }
    this.machineService.getDefaultGoogleProjectId().subscribe(
        {
          next: (success) => {
            if (success && success.length > 0) {
              this.f.projectId.setValue(success[0]);
              this.machine.projectId = success[0];
            }

          }, error: error => {
            const message = this.translateService.instant('Error getting default project id list.');
            this.notificationService.error(message, error, this.COMPONENT_NAME, true);
          }
        }
    );
  }


  private setPossibleMachineGroupNames(): void {
    this.isLoadingMachineGroups = true;
    const groupId: number | null = this.loggedUser.machineGroup;
    const whiteListedMachineryGroups: number[] | null = !this.userRole.isSuperAdminRole
        ? this.loggedUser.getWhiteListedMachineryGroups(true)
        : [];

    this.elasticSearchService.listAllMachineryGroups(groupId, whiteListedMachineryGroups)
    .pipe(takeUntil(this.destroy$)).subscribe(
        {
          next: (success: ElasticSearchResponseEntity) => {
            if (success?.result?.data?.length) {
              this.possibleMachineGroups = success.result.data.filter(t => this.groupIsAvailableForRole(t))
              .sort((a, b) => this.sortingService.sortByName(a, b));
            }
            this.isLoadingMachineGroups = false;
          }, error: error => {
            const message = this.translateService.instant('Error getting machine groups list.');
            this.notificationService.error(message, error, this.COMPONENT_NAME, true);
            this.isLoadingMachineGroups = false;
          }
        }
    );
  }

  private groupIsAvailableForRole(group: MachineGroup): boolean {
    return this.userRole.isSuperAdminRole ? true : !!group.parentName && group.parentId > 0;
  }

  private setPossibleCategories(): void {
    this.isLoadingCategories = true;
    this.machineCategoryService.listMachineCategories().subscribe(
        {
          next: (categories: MachineCategory[]) => {
            this.possibleCategories = categories;
            this.isLoadingCategories = false;
          }, error: error => {
            const message = this.translateService.instant('Error getting categories list.');
            this.notificationService.error(message, error, this.COMPONENT_NAME, true);
            this.isLoadingCategories = false;
          }
        }
    );
  }

  private onAddingMachine(): void {
    if (this.machineCloningInfoNeedsToBeDisplayed()) {
      this.machine.name = this.f.name.value;
      this.machine.group = this.f.group.value;
      this.displayMachineCloningInfo();
    } else {
      if (!this.machine.templateMachine && !!this.machine.machineTag && this.machine.machineTag.length > 0 &&
          !!this.gatewayDetails && !!this.gatewayDetails.id && !!this.gatewayDetails.name && !!this.gatewayDetails.cloudplugType) {
        this.addMachineFromTemplate(true);
      } else {
        this.addMachine(true);
      }
    }
  }

  private machineCloningInfoNeedsToBeDisplayed(): boolean {
    return this.copy;
  }

  private displayMachineCloningInfo(): void {
    const modalRef = this.modalService.open(MachineCloningInfoComponent, {size: 'lg'});
    modalRef.componentInstance.machine = this.machine;
    modalRef.componentInstance.sourceMachineName = this.sourceMachineName;
    modalRef.componentInstance.copySetting = this.copySetting;
    modalRef.componentInstance.usedTables = this.usedTables;
    modalRef.result.then(() => {
      this.addMachine();
    });
  }

  private addMachine(displaySuccessMessage?: boolean) {
    this.loadingIndicator.show(this.translateService.instant('Creating...'));
    const machineBackup = MachineEditComponent.copyMachineProperties(this.machine, null);
    this.updateMachineProperties();
    const addMachineProperties = new MachineAddProperties(this.machine);

    const group = this.machine.group;
    this.machineService
    .addMachine(this.copy, this.copySetting, group, addMachineProperties, this.sourceMachineId, this.firebaseUserId)
    .subscribe(
        {
          next: (addedMachine) => {
            this.activeModal.close(addedMachine);
            this.loadingIndicator.hide();
            if (displaySuccessMessage === true) {
              this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, String(this.machine.id), this.machine.name, ''));
              const message = this.translateService.instant('The following machine is successfully created: ') + this.machine.name.toBold();
              this.notificationService.success(message, this.COMPONENT_NAME, false);
            }
          }, error: (error) => {
            this.handleSaveMachineError(machineBackup, error);
          }
        }
    );
  }

  private addMachineFromTemplate(displaySuccessMessage?: boolean) {
    this.loadingIndicator.show(this.translateService.instant('Creating from template...'));
    const machineBackup = MachineEditComponent.copyMachineProperties(this.machine, null);
    this.updateMachineProperties();
    const addMachineProperties = new MachineAddProperties(this.machine);

    const group = this.machine.group;
    this.machineService
    .addMachineFromTemplate(group, addMachineProperties, this.gatewayDetails)
    .subscribe(
        {
          next: (addedMachine) => {
            this.activeModal.close(addedMachine);
            this.loadingIndicator.hide();
            if (displaySuccessMessage === true) {
              this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, String(this.machine.id), this.machine.name, ''));
              let message = this.translateService.instant('A task is scheduled to create machine %machine, gateway %gatewayName (%gatewayId) and update signals.');
              message = message.replaceAll('%machine', this.machine.type.toBold());
              message = message.replaceAll('%gatewayName', this.gatewayDetails.name.toBold());
              message = message.replaceAll('%gatewayId', this.gatewayDetails.id.toBold());
              this.notificationService.success(message, this.COMPONENT_NAME, false);
            }
          }, error: (error) => {
            this.handleSaveMachineError(machineBackup, error);
          }
        }
    );
  }

  private updateMachine() {
    this.loadingIndicator.show(this.translateService.instant('Saving...'));
    const machineBackup = MachineEditComponent.copyMachineProperties(this.machine, null);
    this.updateMachineProperties();
    this.machineService.updateMachine(this.machine)
    .subscribe({
      next: () => {
        this.updateLeftMenu();
        let message = this.translateService.instant('A task is scheduled to update machine %machine, and if needed adjust its signals.');
        message = message.replaceAll('%machine', this.machine.name.toBold());
        this.activeModal.close(this.machine);
        this.loadingIndicator.hide();
        this.notificationService.success(message, this.COMPONENT_NAME, false);
      },
      error: (error) => {
        this.handleSaveMachineError(machineBackup, error);
      }
    });
  }

  private updateLeftMenu() {
    if (this.initialMachineType !== this.machine.type) {
      this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_NAME, String(this.machine.id), this.initialMachineType, this.machine.type));
      const favoriteNotification = new MenuNotification(OperationType.UPDATE_NAME, String(this.machine.id), '', this.machine.type);
      favoriteNotification.isMachine = true;
      this.favoriteNotificationService.broadcast(favoriteNotification);
    }
    if (this.f.machineTag.dirty) {
      this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_IN_MACHINES_LIST, String(this.machine.id), this.initialMachineType, ''));
    }
  }

  private handleSaveMachineError(machineBackup: Machine, error: any): void {
    this.machine = machineBackup;

    // HTTP 409 -> CONFLICT
    if (error.status === 409) {
      const body = this.translateService.instant('Machine name %s is not unique. Please choose a different one').replace('%s',
          this.machine.name.toBold());
      this.notificationService.error(body, error, this.COMPONENT_NAME, true, this.machineConflictTitle);
    } else if (!error.data || error.status === 403) {
      this.notificationService.error(this.translateService.instant('Error saving machine ') + ': ' + this.machine.name.toBold(),
          error, this.COMPONENT_NAME, true);
    }

    this.activeModal.dismiss('Error adding machine');
    this.loadingIndicator.hide();
  }

  private updateMachineProperties(): void {
    console.log('updateMachineProperties');
    this.machine.type = this.f.type.value;
    this.machine.name = this.f.name.value;
    this.machine.projectId = this.f.projectId.value;
    if (!this.f.category.value.name) {
      this.machine.category = this.f.category.value;
    } else {
      this.machine.category = this.f.category.value.name;
      this.machine.machineCategoryId = this.f.category.value.id;
    }
    this.machine.parentId = this.f.parentId.value;
    this.machine.tableauProjectId = this.f.tableauProjectId.value;
    this.machine.group = this.f.group.value;
    this.machine.machineTag = this.f.machineTag.value;
    this.machine.datasetLocation = this.f.datasetLocation.value;
    this.machine.templateMachine = this.f.isTemplate.value;
  }

  private copyPropertiesFromSelectedMachine(selectedMachine: Machine) {
    this.sourceMachineId = selectedMachine.id !== undefined ? selectedMachine.id : -1;
    this.sourceMachineName = selectedMachine.name;
    this.machine.category = selectedMachine.category;
    this.machine.machineCategoryId = selectedMachine.machineCategoryId;
    this.machine.dataSourcePartitioning = selectedMachine.dataSourcePartitioning;
    this.machine.location = selectedMachine.location;
    this.machine.parentId = selectedMachine.parentId;
    this.machine.projectId = selectedMachine.projectId;
    this.machine.datasetLocation = selectedMachine.datasetLocation;
    this.machine.type = selectedMachine.type;
    this.selectedMachineType = selectedMachine.type;

    this.machineForm.controls.category.setValue(this.machine.category);
    this.machineForm.controls.parentId.setValue(this.machine.parentId);
    this.machineForm.controls.projectId.setValue(this.machine.projectId);
    this.machineForm.controls.datasetLocation.setValue(this.machine.datasetLocation);
    this.machineForm.controls.type.setValue(this.machine.type);
  }

  private copySettingsFromMachine(selectedMachine: Machine, result: ImportSettings) {
    this.copySetting = result.copySettings;
    this.copy = result.copySettings.copy;
    if (!this.copy) {
      return;
    }
    if (this.copySetting.copyTileConfiguration) {
      if (!this.machine.chartConfig) {
        this.machine.chartConfig = new ChartConfig();
      }
      if (selectedMachine.chartConfig) {
        this.machine.chartConfig.dashboard = selectedMachine.chartConfig.dashboard;
      }
    }

    if (this.copySetting.copyStatusIndicator) {
      if (!this.machine.chartConfig) {
        this.machine.chartConfig = new ChartConfig();
      }
      if (selectedMachine.chartConfig) {
        this.machine.chartConfig.statusIndicator = selectedMachine.chartConfig.statusIndicator;
      }
    }

    if (this.copySetting.copyTableauConfiguration) {
      this.machine.tableauViews = selectedMachine.tableauViews;
    }

  }

  private setValidationErrorWhenNameNotUnique() {
    this.f.name.setErrors({used: null});
    this.f.name.updateValueAndValidity();
    this.machines.forEach(m => {
      if (m.name === this.f.name.value) {
        this.f.name.setErrors({used: true});
        this.f.name.updateValueAndValidity();
      }
    });
  }

  private setTables(selectedMachine: Machine) {
    if (selectedMachine.id) {
      this.machineService.listTables(selectedMachine.id).subscribe(
          {
            next: (tables: string[]) => {
              this.usedTables = tables;
              this.isLoadingCategories = false;
            }, error: error => {
              const message = this.translateService.instant('Error getting tables for selected machine.');
              this.notificationService.error(message, error, this.COMPONENT_NAME, true);
            }
          }
      );
    }
  }
}
